import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { useUserAccess } from '../../context/UserAccessContext';
import {
  fetchHubupPublishApprovals,
  updateHubupPublishApproval,
  deleteHubupPublishApproval,
} from '../../api/hubup';
import {
  HUBUP_ANNUARY_CATEGORIES,
  annuaryCategorySelectClass,
  isKnownAnnuaryCategory,
} from '../../constants/hubupAnnuaryCategories';

const STATUS_META = {
  pending: {
    label: 'En attente',
    className: 'bg-amber-100 text-amber-900 border-amber-200',
  },
  approved: {
    label: 'Approuvé',
    className: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  },
  rejected: {
    label: 'Rejeté',
    className: 'bg-red-100 text-red-900 border-red-200',
  },
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente' },
  { value: 'approved', label: 'Approuvé' },
  { value: 'rejected', label: 'Rejeté' },
];

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

function formatParisDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function publicationToForm(item) {
  return {
    serviceName: String(item?.serviceName ?? ''),
    category: String(item?.category ?? ''),
    line1: String(item?.line1 ?? ''),
    line2: String(item?.line2 ?? ''),
    line3: String(item?.line3 ?? ''),
    serviceUrl: String(item?.serviceUrl ?? ''),
    email: String(item?.email ?? ''),
    featured: item?.featured === true,
    status: String(item?.status ?? 'pending').toLowerCase(),
  };
}

function PublishStatusBadge({ status }) {
  const key = String(status ?? '').toLowerCase();
  const meta = STATUS_META[key] || {
    label: status || '—',
    className: 'bg-muted text-muted-foreground border-border',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium shrink-0 ${meta.className}`}
    >
      {meta.label}
    </span>
  );
}

function AnnuaryApprovalCard({ item, onEdit, onDelete }) {
  return (
    <article className="bg-card rounded-lg shadow-elevation-2 border border-border overflow-hidden flex flex-col">
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-base font-semibold text-foreground leading-snug">
            {item.serviceName || 'Sans nom'}
          </h3>
          <PublishStatusBadge status={item.status} />
        </div>

        {item.category ? (
          <p className="text-xs text-muted-foreground mb-3">{item.category}</p>
        ) : null}

        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1 text-sm text-muted-foreground flex-1">
          {item.line1 ? <p>{item.line1}</p> : null}
          {item.line2 ? <p>{item.line2}</p> : null}
          {item.line3 ? <p>{item.line3}</p> : null}
          {!item.line1 && !item.line2 && !item.line3 ? (
            <p className="italic">Aucune description</p>
          ) : null}
        </div>

        <div className="mt-4 pt-3 border-t border-border space-y-2 text-xs text-muted-foreground">
          {item.email ? (
            <p className="flex items-center gap-1.5">
              <Icon name="Mail" size={14} className="shrink-0 opacity-70" />
              <a
                href={`mailto:${item.email}`}
                className="text-foreground hover:underline truncate"
              >
                {item.email}
              </a>
            </p>
          ) : null}
          {item.serviceUrl ? (
            <p className="flex items-start gap-1.5">
              <Icon name="ExternalLink" size={14} className="shrink-0 opacity-70 mt-0.5" />
              <a
                href={item.serviceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {item.serviceUrl}
              </a>
            </p>
          ) : null}
          {item.featured ? (
            <p className="inline-flex items-center gap-1 text-amber-700 font-medium">
              <Icon name="Star" size={14} />
              Mise en avant demandée
            </p>
          ) : null}
          {item.hubupAccountRequested ? (
            <p
              className={`inline-flex items-center gap-1 font-medium ${
                item.hubupAccountCreatedAt
                  ? 'text-emerald-700'
                  : item.hubupAccountExisting || item.hubupAccountEmailVerified
                    ? 'text-blue-700'
                    : 'text-amber-700'
              }`}
            >
              <Icon name="UserPlus" size={14} />
              {item.hubupAccountCreatedAt
                ? item.hubupAccountExisting
                  ? 'Compte HubUP rattaché'
                  : 'Compte HubUP créé'
                : item.hubupAccountExisting
                  ? 'Compte HubUP existant'
                  : item.hubupAccountEmailVerified
                    ? 'Compte HubUP confirmé'
                    : 'Compte HubUP à confirmer'}
            </p>
          ) : null}
          {item.hubupAccountError ? (
            <p className="text-red-700 font-medium">
              Erreur compte HubUP : {item.hubupAccountError}
            </p>
          ) : null}
          <p>Créé le {formatParisDate(item.createdAt)}</p>
          {item.updatedAt && item.updatedAt !== item.createdAt ? (
            <p>Mis à jour le {formatParisDate(item.updatedAt)}</p>
          ) : null}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="danger"
            size="sm"
            iconName="Trash2"
            iconPosition="left"
            onClick={() => onDelete(item)}
          >
            Supprimer
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconName="Pencil"
            iconPosition="left"
            onClick={() => onEdit(item)}
          >
            Modifier
          </Button>
        </div>
      </div>
    </article>
  );
}

const HubupAnnuaryApprovals = () => {
  const { groupId } = useParams();
  const { user } = useUserAccess();
  const isAdmin = user?.permission === 'admin';

  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHubupPublishApprovals();
      setPublications(res?.data?.publications || []);
    } catch (e) {
      setError(e?.message || 'Impossible de charger les demandes annuaire.');
      setPublications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  useEffect(() => {
    if (!editing && !confirmDelete) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [editing, confirmDelete]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return publications.filter((p) => {
      const statusOk =
        statusFilter === 'all' ||
        String(p.status ?? '').toLowerCase() === statusFilter;
      if (!statusOk) return false;
      if (!q) return true;
      return String(p.serviceName ?? '').toLowerCase().includes(q);
    });
  }, [publications, statusFilter, searchQuery]);

  const counts = useMemo(() => {
    const c = { all: publications.length, pending: 0, approved: 0, rejected: 0 };
    for (const p of publications) {
      const s = String(p.status ?? '').toLowerCase();
      if (s in c) c[s] += 1;
    }
    return c;
  }, [publications]);

  const openEdit = (item) => {
    setEditing(item);
    setForm(publicationToForm(item));
    setFormError(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setForm(null);
    setFormError(null);
  };

  const openDeleteConfirm = (item) => {
    setConfirmDelete(item);
    setDeleteError(null);
  };

  const closeDeleteConfirm = () => {
    setConfirmDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete?.id) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteHubupPublishApproval(confirmDelete.id);
      setPublications((prev) => prev.filter((p) => p.id !== confirmDelete.id));
      if (editing?.id === confirmDelete.id) closeEdit();
      closeDeleteConfirm();
    } catch (err) {
      setDeleteError(err?.message || 'Suppression impossible.');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!editing?.id || !form) return;
    const category = form.category.trim();
    if (!category) {
      setFormError('La catégorie est obligatoire.');
      return;
    }
    if (!isKnownAnnuaryCategory(category)) {
      setFormError('Choisissez une catégorie dans la liste.');
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      const res = await updateHubupPublishApproval(editing.id, {
        serviceName: form.serviceName.trim(),
        category: form.category.trim(),
        line1: form.line1.trim(),
        line2: form.line2.trim(),
        line3: form.line3.trim(),
        serviceUrl: form.serviceUrl.trim(),
        email: form.email.trim(),
        featured: form.featured,
        status: form.status,
      });
      const updated = res?.data?.publication;
      if (updated?.id) {
        setPublications((prev) =>
          prev.map((p) => (p.id === updated.id ? updated : p)),
        );
      } else {
        await load();
      }
      closeEdit();
    } catch (err) {
      setFormError(err?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to={`/hubup/g/${groupId}/dashboard`} replace />;
  }

  return (
    <>
      <Helmet>
        <title>Approbation annuaire - HubUP</title>
      </Helmet>

      <main className="main-content">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Approbation annuaire
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Demandes de publication sur l&apos;annuaire HubUP (Landing Page).
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto lg:min-w-[28rem]">
            <div className="relative flex-1">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par nom de service…"
                className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Rechercher par nom de service"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={load}
              disabled={loading}
              className="shrink-0"
            >
              Actualiser
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'all', label: `Toutes (${counts.all})` },
            { id: 'pending', label: `En attente (${counts.pending})` },
            { id: 'approved', label: `Approuvées (${counts.approved})` },
            { id: 'rejected', label: `Rejetées (${counts.rejected})` },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                statusFilter === f.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:bg-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-900 px-4 py-3 text-sm mb-6">
            {error}
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-12">
            Chargement des demandes…
          </p>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            Aucune demande
            {statusFilter !== 'all' || searchQuery.trim() ? ' pour ces critères' : ''}.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {filtered.map((item) => (
              <AnnuaryApprovalCard
                key={item.id}
                item={item}
                onEdit={openEdit}
                onDelete={openDeleteConfirm}
              />
            ))}
          </div>
        )}
      </main>

      {editing && form ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto border border-border my-auto"
            role="dialog"
            aria-labelledby="edit-publication-title"
          >
            <div className="p-4 md:p-6 border-b border-border">
              <h2 id="edit-publication-title" className="text-lg font-semibold text-foreground">
                Modifier la demande
              </h2>
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {editing.serviceName || editing.id}
              </p>
            </div>
            <form onSubmit={handleSave} className="p-4 md:p-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Statut
                </label>
                <select
                  className={inputClass}
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value }))
                  }
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Nom du service
                </label>
                <input
                  type="text"
                  className={inputClass}
                  value={form.serviceName}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, serviceName: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Catégorie <span className="text-error">*</span>
                </label>
                <select
                  required
                  className={annuaryCategorySelectClass}
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value }))
                  }
                >
                  <option value="">Choisir une catégorie…</option>
                  {form.category && !isKnownAnnuaryCategory(form.category) ? (
                    <option value={form.category}>{form.category}</option>
                  ) : null}
                  {HUBUP_ANNUARY_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              {['line1', 'line2', 'line3'].map((key, i) => (
                <div key={key}>
                  <label className="text-xs font-medium text-foreground mb-1 block">
                    Ligne {i + 1}
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={form[key]}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, [key]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  URL du service
                </label>
                <input
                  type="url"
                  className={inputClass}
                  value={form.serviceUrl}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, serviceUrl: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1 block">
                  Email
                </label>
                <input
                  type="email"
                  className={inputClass}
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </div>
              <Checkbox
                checked={form.featured}
                onChange={(e) =>
                  setForm((f) => ({ ...f, featured: e.target.checked }))
                }
                label="Mise en avant (featured)"
              />
              {formError ? (
                <p className="text-xs text-error">{formError}</p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeEdit}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="success"
                  iconName="Save"
                  iconPosition="left"
                  disabled={saving}
                >
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-md border border-border my-auto"
            role="alertdialog"
            aria-labelledby="delete-publication-title"
          >
            <div className="p-4 md:p-6 border-b border-border">
              <h2 id="delete-publication-title" className="text-lg font-semibold text-foreground">
                Supprimer la demande ?
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                Cette action est définitive. La publication{' '}
                <span className="font-medium text-foreground">
                  {confirmDelete.serviceName?.trim() || 'cette annonce'}
                </span>{' '}
                sera retirée de l&apos;annuaire.
              </p>
            </div>
            <div className="p-4 md:p-6 space-y-4">
              {deleteError ? (
                <p className="text-xs text-error">{deleteError}</p>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeDeleteConfirm}
                  disabled={deleting}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Suppression…' : 'Supprimer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default HubupAnnuaryApprovals;
